<script lang="ts">
  /**
   * 書いて AC（card-schema.md §3.7、judge.md §3、components.md §1.6）。
   * 「実行」は標準入力欄の 1 ケースだけを走らせる（採点しない）。「採点する」は全ケースを 1 リクエストで送る。
   */
  import { onDestroy, untrack } from 'svelte';
  import type { WriteAcCard } from '../../lib/cards';
  import type { RunResponse } from '../../lib/sandboxd';
  import CardShell from '../CardShell.svelte';
  import CodeBlock from '../CodeBlock.svelte';
  import CodeEditor from '../CodeEditor.svelte';
  import InlineMarkdown from '../InlineMarkdown.svelte';
  import OutputPanel from '../OutputPanel.svelte';
  import VerdictBadge from '../VerdictBadge.svelte';
  import { SUBMIT_CHORD_LABEL } from '../keyboard';
  import type { GradeResult } from '../types';
  import type { WriteAcAnswer } from './answer';
  import { gradeOnSandbox, runOnce } from './run';
  import { RunState } from './run-state.svelte';
  import RunStatus from './RunStatus.svelte';
  import { caseLabel, compileErrorLine, firstFailure, writeAcResult } from './write-ac';

  interface Props {
    card: WriteAcCard;
    index: number;
    total: number;
    box?: number;
    result?: GradeResult | undefined;
    answer?: WriteAcAnswer | undefined;
    showUseCase?: boolean;
    /** CardShell の globalKeys（1 画面 1 カードの画面だけ true） */
    globalKeys?: boolean;
    /**
     * 採点後にコードを直したら、もう一度採点できるようにする（レッスンの書く課題）。
     * ドリルは採点したら次へ進む流儀なので出さない（既定 false）。
     */
    allowRegrade?: boolean;
    ongrade?: ((result: GradeResult, answer: WriteAcAnswer) => void) | undefined;
    onnext?: (() => void) | undefined;
    onprev?: (() => void) | undefined;
    onabort?: (() => void) | undefined;
    /** ヒントを開いたとき（level は開いた段階、1 始まり） */
    onhint?: ((level: number) => void) | undefined;
    /** 書きかけのコード（初期表示用。回答 answer があればそちらを出す） */
    draft?: string | undefined;
    /**
     * 書きかけの保存先（レッスンの書く課題）。編集が 1 秒止まったら呼ぶ。
     * ページを離れる（pagehide / visibilitychange で hidden）・画面を閉じるときは待たずに呼ぶ。
     */
    ondraft?: ((code: string) => void) | undefined;
  }

  let {
    card,
    index,
    total,
    box = 0,
    result,
    answer: savedAnswer,
    showUseCase = false,
    globalKeys = false,
    allowRegrade = false,
    ongrade,
    onnext,
    onprev,
    onabort,
    onhint,
    draft,
    ondraft,
  }: Props = $props();

  const uid = $props.id();
  let code = $state('');
  let stdin = $state('');
  let gradedAnswer = $state<WriteAcAnswer | undefined>();
  const shown = $derived(gradedAnswer ?? savedAnswer);
  /** 「実行」の結果（採点しない） */
  let trial = $state<RunResponse | null>(null);
  /** 実行中なのが採点か「実行」か */
  let mode = $state<'grade' | 'run' | null>(null);
  /** 「再実行」で繰り返す操作 */
  let lastAction: 'grade' | 'run' = 'grade';

  /** 書きかけの保存（1 秒 debounce）。保存先はその編集をした時点の ondraft（カードが替わっても混ざらない） */
  const DRAFT_DELAY_MS = 1000;
  let draftSaved = '';
  let draftPending: { code: string; save: (code: string) => void } | null = null;
  let draftTimer: ReturnType<typeof setTimeout> | undefined;

  function flushDraft() {
    clearTimeout(draftTimer);
    draftTimer = undefined;
    const p = draftPending;
    draftPending = null;
    if (!p) return;
    draftSaved = p.code;
    p.save(p.code);
  }

  function scheduleDraft(next: string, save: ((code: string) => void) | undefined) {
    if (next === draftSaved || !save) {
      clearTimeout(draftTimer);
      draftTimer = undefined;
      draftPending = null;
      return;
    }
    draftPending = { code: next, save };
    clearTimeout(draftTimer);
    draftTimer = setTimeout(flushDraft, DRAFT_DELAY_MS);
  }

  let lastKey = '';
  $effect.pre(() => {
    const key = `${card.id}:${index}`;
    if (key === lastKey) return;
    lastKey = key;
    untrack(flushDraft);
    code = savedAnswer?.code ?? draft ?? card.starter;
    draftSaved = code;
    stdin = card.cases[0]?.in ?? '';
    trial = null;
    gradedAnswer = undefined;
  });

  $effect(() => {
    const next = code;
    const save = ondraft;
    untrack(() => scheduleDraft(next, save));
  });

  $effect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flushDraft();
    };
    window.addEventListener('pagehide', flushDraft);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', flushDraft);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  });

  const run = new RunState();
  onDestroy(() => {
    flushDraft();
    run.dispose();
  });

  /** 採点したコードから書き換えた（allowRegrade のときだけ再採点を受け付ける） */
  const edited = $derived(
    allowRegrade && result !== undefined && shown !== undefined && code !== shown.code,
  );
  const graded = $derived(result !== undefined && !edited);
  const gradeState = $derived(
    graded
      ? 'graded'
      : run.running && mode === 'grade'
        ? 'grading'
        : code !== card.starter
          ? 'answering'
          : 'idle',
  );
  const canSubmit = $derived(code.trim() !== '' && !run.running);

  async function submit() {
    if (graded || run.running || code.trim() === '') return;
    mode = 'grade';
    lastAction = 'grade';
    const snapshot = code;
    const judge = await run.start((hooks) => gradeOnSandbox(card, snapshot, card.cases, hooks));
    mode = null;
    if (!judge) return;
    const a: WriteAcAnswer = { kind: 'write-ac', code: snapshot, judge };
    gradedAnswer = a;
    ongrade?.(writeAcResult(judge, card.cases), a);
  }

  async function tryRun() {
    if (run.running || code.trim() === '') return;
    mode = 'run';
    lastAction = 'run';
    trial = null;
    const res = await run.start((hooks) => runOnce(code, stdin, hooks));
    mode = null;
    if (res) trial = res;
  }

  function retry() {
    if (lastAction === 'run') void tryRun();
    else void submit();
  }

  const judge = $derived(graded ? shown?.judge : undefined);
  const failure = $derived(judge ? firstFailure(judge) : undefined);
  const ceLines = $derived.by(() => {
    const out = judge?.overall === 'CE' ? judge.compileOutput : '';
    const lines: { line: number; message: string }[] = [];
    for (const m of out.matchAll(/^main\.swift:(\d+):\d+: error: (.*)$/gm)) {
      lines.push({ line: Number(m[1]), message: m[2] ?? '' });
    }
    return lines;
  });
  const trialRun = $derived(trial?.runs?.[0]);
  const trialCe = $derived(
    trial && trial.compile?.ok === false ? (trial.compile.output ?? '') : null,
  );
  const firstCase = $derived(card.cases[0]);
</script>

<CardShell
  {globalKeys}
  {card}
  {index}
  {total}
  state={gradeState}
  {result}
  {box}
  prompt={card.title}
  {showUseCase}
  submitKeys={[SUBMIT_CHORD_LABEL]}
  {canSubmit}
  onsubmit={() => void submit()}
  {onnext}
  {onprev}
  {onabort}
  {onhint}
>
  <div class="statement" data-testid="statement">
    <InlineMarkdown text={card.statement} block />
  </div>

  {#if firstCase}
    <div class="sample">
      <div>
        <h2 class="sub">入力例</h2>
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <pre class="output" tabindex="0" aria-label="入力例">{firstCase.in.replace(/\n$/, '') ||
            '（なし）'}</pre>
      </div>
      <div>
        <h2 class="sub">出力例</h2>
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <pre class="output" tabindex="0" aria-label="出力例">{firstCase.out.replace(
            /\n$/,
            '',
          )}</pre>
      </div>
    </div>
  {/if}

  <CodeEditor
    bind:value={code}
    id="{uid}-editor"
    readonly={run.running}
    minLines={8}
    errorLines={ceLines}
    onsubmit={() => void submit()}
  />

  <div class="trial">
    <label class="sub" for="{uid}-stdin">標準入力（「実行」で使う）</label>
    <textarea
      id="{uid}-stdin"
      class="stdin"
      rows="3"
      spellcheck="false"
      autocapitalize="off"
      autocomplete="off"
      data-testid="stdin"
      bind:value={stdin}></textarea>
    <div class="trial-actions">
      <button
        class="btn"
        type="button"
        data-testid="run"
        disabled={run.running || code.trim() === ''}
        aria-busy={run.running && mode === 'run' ? 'true' : undefined}
        onclick={() => void tryRun()}>{run.running && mode === 'run' ? '実行中' : '実行'}</button
      >
      <span class="note">採点には全 {card.cases.length} ケースを使います。</span>
    </div>
  </div>

  <RunStatus {run} busyLabel={mode === 'run' ? '実行中' : '判定中'} onretry={retry} />

  {#if trial}
    <div class="trial-result" data-testid="trial-result">
      {#if trialCe !== null}
        <h2 class="sub">コンパイラの出力</h2>
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <pre class="output" tabindex="0" aria-label="コンパイラの出力">{trialCe}</pre>
      {:else if trialRun}
        {#if trialRun.timedOut}
          <p class="note">
            <VerdictBadge verdict="TLE" size="sm" /> 3 秒以内に終わりませんでした。
          </p>
        {:else if trialRun.exitCode !== 0}
          <p class="note"><VerdictBadge verdict="RE" size="sm" /> 実行の途中で止まりました。</p>
        {/if}
        <OutputPanel
          mode="plain"
          stdout={trialRun.stdout}
          stderr={trialRun.stderr}
          durationMs={trialRun.durationMs}
        />
      {/if}
    </div>
  {/if}

  {#snippet feedback()}
    {#if judge}
      <ol class="cases" aria-label="ケースごとの判定" data-testid="case-verdicts">
        {#each judge.cases as c (c.index)}
          <li>
            <VerdictBadge
              verdict={c.verdict}
              size="sm"
              caseLabel={caseLabel(card.cases, c.index)}
            />
            <span>{caseLabel(card.cases, c.index)}</span>
            {#if c.durationMs !== null}<span class="num ms">{c.durationMs} ms</span>{/if}
          </li>
        {/each}
      </ol>
      {#if judge.overall === 'CE'}
        {@const line = compileErrorLine(judge.compileOutput)}
        <h3 class="sub">コンパイラの出力{line ? `（${line} 行目）` : ''}</h3>
        <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
        <pre class="output" tabindex="0" aria-label="コンパイラの出力">{judge.compileOutput}</pre>
      {:else if failure?.verdict === 'WA'}
        <OutputPanel
          mode="diff"
          expected={card.cases[failure.index]?.out ?? ''}
          actual={failure.stdout}
          stderr={failure.stderr}
          {...failure.durationMs !== null ? { durationMs: failure.durationMs } : {}}
        />
      {:else if failure}
        <OutputPanel
          mode="plain"
          stdout={failure.stdout}
          stderr={failure.stderr}
          {...failure.durationMs !== null ? { durationMs: failure.durationMs } : {}}
        />
      {/if}
      {#if judge.overall === 'AC'}
        <details class="solution" data-testid="solution">
          <summary>模範解答を見る</summary>
          <CodeBlock code={card.solution} label="模範解答" />
        </details>
      {:else}
        <p class="note">コードは書き直して「実行」で確かめられます。</p>
      {/if}
    {/if}
  {/snippet}
</CardShell>

<style>
  .statement {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .sub {
    display: block;
    font-size: var(--fs-sm);
    color: var(--text-soft);
    font-weight: var(--fw-semibold);
    margin-bottom: var(--space-1);
  }
  .sample {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: var(--space-3);
  }
  .trial {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .stdin {
    font-family: var(--font-mono);
    font-size: var(--fs-code);
  }
  .trial-actions {
    display: flex;
    align-items: center;
    gap: var(--space-3);
    flex-wrap: wrap;
  }
  .note {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--fs-sm);
    color: var(--text-soft);
  }
  .trial-result {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
  }
  .cases {
    list-style: none;
    margin: 0;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: var(--space-2) var(--space-4);
  }
  .cases li {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--fs-sm);
  }
  .ms {
    color: var(--text-soft);
  }
  .solution summary {
    cursor: pointer;
    color: var(--link);
    margin-bottom: var(--space-2);
  }
</style>
